/**
 * Split Payment API Handler
 * This handles the API calls directly from the browser using Shopify's Storefront API
 * No external server needed!
 */

// Payment availability configuration
const PAYMENT_AVAILABILITY = {
  klarna: [
    'AT', 'BE', 'NO', 'SE', 'CH', 'DK', 'CZ', 'PL', 'FI', 'DE',
    'FR', 'NL', 'IT', 'ES', 'PT', 'GR', 'RO', 'US', 'MX', 'CA',
    'AU', 'NZ', 'IE', 'GB', 'UK'
  ],
  clearpay: [
    'AU', 'NZ', 'CA', 'US', 'GB', 'UK'
  ],
  paypal: 'all',
  card: 'all'
};

class SplitPaymentAPI {
  constructor() {
    this.storefrontToken = '107e40eef6eb6c9db6cd4bdf75b3e4dd';
    this.adminToken = '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';
    this.shopDomain = window.Shopify.shop;
    this.apiVersion = '2024-01';
  }

  /**
   * Create draft orders using Admin API via proxy
   * Since we can't call Admin API directly from browser, we'll use a workaround
   */
  async createDraftOrders(splitConfig, cartData) {
    try {
      // For now, we'll save the split configuration to cart attributes
      // Then use Reactflow to create the actual draft orders

      const splitGroupId = this.generateGroupId();
      const parentOrderNumber = Date.now().toString().slice(-7);

      // Save split configuration to cart attributes
      // IMPORTANT: Preserve the existing cart note if present
      const cartAttributes = {
        attributes: {
          'split_payment': 'true',
          'split_group_id': splitGroupId,
          'split_count': splitConfig.length.toString(),
          'split_config': JSON.stringify(splitConfig),
          'parent_order': parentOrderNumber
        }
      };

      // Preserve existing cart note if present
      if (cartData.note) {
        cartAttributes.note = cartData.note;
      }

      // Update cart with split payment attributes
      const cartResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartAttributes)
      });

      if (!cartResponse.ok) {
        throw new Error('Failed to update cart attributes');
      }

      // Create draft order notes for each split
      const draftOrders = [];
      for (let i = 0; i < splitConfig.length; i++) {
        const split = splitConfig[i];

        // For testing, create mock draft order data
        // In production, Reactflow will create actual draft orders
        draftOrders.push({
          id: `draft_${splitGroupId}_${i + 1}`,
          splitNumber: i + 1,
          totalSplits: splitConfig.length,
          amount: split.amount,
          method: split.type,
          provider: this.getProviderName(split.type),
          // Temporary checkout URL - will be replaced by Reactflow
          invoiceUrl: `/checkout?split=${i + 1}&group=${splitGroupId}`,
          status: 'pending',
          groupId: splitGroupId
        });
      }

      // Trigger Reactflow webhook to create actual draft orders
      const webhookResult = await this.triggerReactflowWebhook(splitGroupId, splitConfig, cartData);

      // Check if we got a pending/processing status (Reactflow is processing)
      if (webhookResult && typeof webhookResult === 'object' &&
          (webhookResult.status === 'pending' || webhookResult.status === 'processing')) {
        // Reactflow is processing asynchronously
        console.log('Reactflow processing split payments asynchronously');
        return {
          status: webhookResult.status, // Keep the original status
          groupId: webhookResult.groupId || splitGroupId,
          message: webhookResult.message || 'Processing split payments...',
          success: false, // Mark as false so frontend knows to poll
          queryAfter: webhookResult.queryAfter || 3000 // Pass along the query timing
        };
      }

      // Check if we got a full response from Reactflow
      if (webhookResult && typeof webhookResult === 'object' && webhookResult.success) {
        // We got actual draft orders from Reactflow!
        const actualDraftOrders = webhookResult.draftOrders || [];
        const invoiceUrls = webhookResult.summary?.invoiceUrls || [];

        // Store the complete data for tracking
        const splitPaymentData = {
          groupId: splitGroupId,
          parentOrderNumber: parentOrderNumber,
          draftOrders: actualDraftOrders,
          invoiceUrls: invoiceUrls,
          splitConfig: splitConfig,
          createdAt: new Date().toISOString(),
          status: 'ready_for_payment',
          totalAmount: webhookResult.summary?.totalAmount || 0,
          allOrdersCreated: webhookResult.summary?.allOrdersCreated || false
        };

        sessionStorage.setItem('splitPaymentData_' + splitGroupId, JSON.stringify(splitPaymentData));

        // Return with actual invoice URLs
        return {
          success: true,
          draftOrders: actualDraftOrders,
          invoiceUrls: invoiceUrls,
          firstPaymentUrl: invoiceUrls[0]?.url || '/checkout',
          groupId: splitGroupId,
          parentOrderNumber: parentOrderNumber,
          message: 'Split orders created successfully!',
          showConfirmation: true, // Flag to show confirmation modal
          totalAmount: webhookResult.summary?.totalAmount || 0
        };
      }

      // Fallback if webhook didn't return full data
      // Store split payment data in session for tracking
      const splitPaymentData = {
        groupId: splitGroupId,
        parentOrderNumber: parentOrderNumber,
        draftOrders: draftOrders,
        splitConfig: splitConfig,
        createdAt: new Date().toISOString(),
        status: 'initiated',
        cleanupNeeded: true,
        discountCodes: []
      };

      sessionStorage.setItem('splitPaymentData_' + splitGroupId, JSON.stringify(splitPaymentData));

      // Track active groups
      let activeGroups = JSON.parse(sessionStorage.getItem('activeSplitGroups') || '[]');
      if (!activeGroups.includes(splitGroupId)) {
        activeGroups.push(splitGroupId);
        sessionStorage.setItem('activeSplitGroups', JSON.stringify(activeGroups));
      }

      return {
        success: true,
        draftOrders: draftOrders,
        firstPaymentUrl: '/checkout',
        groupId: splitGroupId,
        parentOrderNumber: parentOrderNumber,
        message: webhookResult
          ? 'Split payment configuration saved. Redirecting to checkout...'
          : 'Split payment saved. Complete checkout and we\'ll process your split payments.',
        showConfirmation: false
      };

    } catch (error) {
      console.error('Error creating draft orders:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Trigger Reactflow to create actual draft orders
   */
  async triggerReactflowWebhook(groupId, splitConfig, cartData) {
    // Using Cloudflare Worker to forward to Reactflow
    const reactflowUrl = 'https://split-payment-proxy.ainnlondon.workers.dev';

    try {
      // Don't add any placeholder email - Shopify draft orders work fine without email

      // Log what we're sending for debugging
      const webhookPayload = {
        action: 'create_split_orders',
        groupId: groupId,
        config: splitConfig,
        cart: cartData,
        shop: window.Shopify.shop,
        timestamp: new Date().toISOString(),
        requireCleanup: true // Flag to indicate discount codes will need cleanup
      };

      console.log('Sending to webhook:', webhookPayload);
      console.log('Cart customer info:', {
        customer_id: cartData.customer_id,
        email: cartData.email,
        customer: cartData.customer,
        note: cartData.note,
        original_customer_note: cartData.original_customer_note
      });

      // Debug: Log if we have a customer note
      if (cartData.note) {
        console.log('[Split Payment API] Customer note found:', cartData.note);
      } else {
        console.log('[Split Payment API] No customer note in cart');
      }

      // Request goes through Cloudflare Worker proxy - no CORS issues!
      const response = await fetch(reactflowUrl, {
        method: 'POST',
        mode: 'cors', // Explicitly set CORS mode
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Parse the Reactflow response
      const responseText = await response.text();
      console.log('Raw response text from proxy:', responseText);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      let reactflowData;
      try {
        reactflowData = JSON.parse(responseText);
        console.log('Parsed response structure:', {
          hasCode: 'code' in reactflowData,
          hasMessage: 'message' in reactflowData,
          hasData: 'data' in reactflowData,
          hasSuccess: 'success' in reactflowData,
          hasDraftOrders: 'draftOrders' in reactflowData
        });
        console.log('Parsed Reactflow response:', reactflowData);
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        console.log('Response was:', responseText);
        throw new Error('Invalid response from webhook');
      }

      // Check if this is the proxy's wrapper response
      if (reactflowData.code && reactflowData.message && reactflowData.data !== undefined) {
        // This looks like the proxy's response wrapper, extract the actual data
        console.log('Detected proxy wrapper, extracting data...');
        reactflowData = reactflowData.data || {};
      }

      // Store the invoice URLs and payment details in session
      if (reactflowData && reactflowData.success && reactflowData.draftOrders) {
        const paymentTracking = {
          groupId: reactflowData.groupId,
          totalSplits: reactflowData.totalCreated,
          currentSplit: 0,
          completedSplits: [],
          draftOrders: reactflowData.draftOrders,
          invoiceUrls: reactflowData.summary?.invoiceUrls || [],
          originalCart: cartData,
          status: 'ready',
          createdAt: new Date().toISOString()
        };

        sessionStorage.setItem('splitPaymentTracking', JSON.stringify(paymentTracking));
        sessionStorage.setItem('splitPaymentTracking_' + groupId, JSON.stringify(paymentTracking));

        return reactflowData;
      } else {
        // Even if the structure doesn't match exactly, return what we got
        console.log('Reactflow data structure unexpected, returning raw data');
        return reactflowData;
      }

      console.log('Reactflow webhook triggered but no data returned');
      return reactflowData || true;
    } catch (error) {
      console.error('Failed to trigger Reactflow webhook:', error);

      // If CORS fails, we need an alternative approach
      // Option 1: Use cart note to store the data and have Reactflow monitor cart updates
      // Option 2: Create a proxy endpoint on your Shopify app
      // Option 3: Use a different webhook service that supports CORS

      // For now, we'll store the data in cart note for Reactflow to pick up
      console.log('Falling back to cart note method...');
      await this.saveToCartNote(groupId, splitConfig, cartData);

      return false;
    }
  }

  /**
   * Fallback: Save split configuration to cart attributes (preserves user's cart note)
   */
  async saveToCartNote(groupId, splitConfig, cartData) {
    try {
      // Use cart attributes with underscore prefix to avoid conflicts
      // This preserves the user's cart note and any discount codes
      const cartUpdate = {
        attributes: {
          '_split_payment_active': 'true',
          '_split_group_id': groupId,
          '_split_config': JSON.stringify(splitConfig),
          '_split_timestamp': new Date().toISOString(),
          '_split_total': cartData.total_price.toString()
        }
      };

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartUpdate)
      });

      if (!response.ok) {
        throw new Error('Failed to update cart attributes');
      }

      console.log('Split payment configuration saved to cart attributes (preserving user note)');
      return true;
    } catch (error) {
      console.error('Failed to save to cart attributes:', error);
      return false;
    }
  }

  /**
   * Alternative: Create draft order using a form submission
   * This approach uses Shopify's built-in draft order creation
   */
  async createDraftOrderViaForm(splitConfig) {
    // Create a hidden form that submits to Shopify's draft order endpoint
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/cart';

    // Add split payment data as cart attributes
    const attributes = {
      'split_payment': 'true',
      'split_config': JSON.stringify(splitConfig)
    };

    for (const [key, value] of Object.entries(attributes)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `attributes[${key}]`;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Detect user's country code
   */
  async detectUserCountry() {
    // Check cache first
    const cached = sessionStorage.getItem('userCountry');
    if (cached) {
      const data = JSON.parse(cached);
      // Cache for 30 minutes
      if (Date.now() - data.timestamp < 30 * 60 * 1000) {
        console.log('Using cached country:', data.country);
        return data.country;
      }
    }

    let countryCode = null;

    // Method 1: Check Shopify's localization
    if (window.Shopify?.country) {
      countryCode = window.Shopify.country;
      console.log('Country from Shopify:', countryCode);
    }

    // Method 2: Check customer's saved address (logged in users)
    if (!countryCode) {
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        if (cart.customer?.default_address?.country_code) {
          countryCode = cart.customer.default_address.country_code;
          console.log('Country from customer address:', countryCode);
        }
      } catch (e) {
        console.log('Could not get customer address');
      }
    }

    // Method 3: Check browser language as hint
    if (!countryCode && navigator.language) {
      const lang = navigator.language.split('-')[1]; // e.g., en-US -> US
      if (lang) {
        countryCode = lang.toUpperCase();
        console.log('Country hint from browser language:', countryCode);
      }
    }

    // Method 4: Use IP geolocation as fallback
    if (!countryCode) {
      try {
        const geo = await fetch('https://ipapi.co/json/').then(r => r.json());
        countryCode = geo.country_code;
        console.log('Country from IP geolocation:', countryCode);
      } catch (e) {
        console.log('IP geolocation failed, defaulting to GB');
        countryCode = 'GB'; // Default to UK
      }
    }

    // Cache the result
    sessionStorage.setItem('userCountry', JSON.stringify({
      country: countryCode,
      timestamp: Date.now()
    }));

    return countryCode;
  }

  /**
   * Get available payment methods based on user's country
   */
  async getAvailablePaymentMethods() {
    // Detect user's country
    const countryCode = await this.detectUserCountry();
    console.log('Determining payment methods for country:', countryCode);

    // Get cart data
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    // Always available payment methods
    const methods = [
      {
        type: 'card',
        name: 'Credit/Debit Card',
        provider: 'Shopify Payments',
        available: true,
        minAmount: 0,
        maxAmount: null
      },
      {
        type: 'paypal',
        name: 'PayPal',
        provider: 'PayPal',
        available: true,
        minAmount: 0,
        maxAmount: null
      }
    ];

    // Check if Klarna is available in this country
    if (PAYMENT_AVAILABILITY.klarna.includes(countryCode)) {
      methods.push({
        type: 'klarna',
        name: 'Klarna',
        provider: 'Klarna',
        available: true,
        minAmount: 35,
        maxAmount: 1500,
        requiresLimit: true
      });
      console.log('Klarna is available in', countryCode);
    }

    // Check if Clearpay is available in this country
    if (PAYMENT_AVAILABILITY.clearpay.includes(countryCode)) {
      methods.push({
        type: 'clearpay',
        name: 'Clearpay',
        provider: 'Clearpay',
        available: true,
        minAmount: 30,
        maxAmount: 1000,
        requiresLimit: true
      });
      console.log('Clearpay is available in', countryCode);
    }

    console.log('Available payment methods for', countryCode, ':', methods.map(m => m.type));

    return methods;
  }

  /**
   * Helper: Generate unique group ID
   */
  generateGroupId() {
    return 'split_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Helper: Get provider name
   */
  getProviderName(type) {
    const providers = {
      'giftcard': 'Gift Card',
      'card': 'Shopify Payments',
      'paypal': 'PayPal',
      'klarna': 'Klarna',
      'clearpay': 'Clearpay'
    };
    return providers[type] || type;
  }

  /**
   * Process continuation from previous payment
   */
  async processContinuation() {
    // Check URL parameters for split payment continuation
    const urlParams = new URLSearchParams(window.location.search);
    const splitNumber = urlParams.get('split');
    const groupId = urlParams.get('group');

    if (splitNumber && groupId) {
      // Get split configuration from session
      const session = sessionStorage.getItem('split_payment_session');
      if (session) {
        const data = JSON.parse(session);

        // Show progress message
        this.showProgressMessage(splitNumber, data.splits);

        // Auto-redirect to next payment if needed
        if (splitNumber < data.splits) {
          setTimeout(() => {
            window.location.href = `/checkout?split=${parseInt(splitNumber) + 1}&group=${groupId}`;
          }, 3000);
        }
      }
    }
  }

  /**
   * Show progress message
   */
  showProgressMessage(current, total) {
    const message = document.createElement('div');
    message.className = 'split-payment-progress';
    message.innerHTML = `
      <div style="position: fixed; top: 20px; right: 20px; background: #4CAF50; color: white; padding: 15px 20px; border-radius: 5px; z-index: 9999;">
        Payment ${current} of ${total} completed! Redirecting to next payment...
      </div>
    `;
    document.body.appendChild(message);
  }

  /**
   * Check for master order using Admin API via proxy
   */
  async checkForMasterOrder(groupId) {
    try {
      const proxyUrl = 'https://split-payment-proxy.ainnlondon.workers.dev';

      const response = await fetch(`${proxyUrl}/check-master-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shop-Domain': this.shopDomain
        },
        body: JSON.stringify({
          groupId: groupId,
          adminToken: this.adminToken
        })
      });

      if (!response.ok) {
        return { success: false, masterOrder: null };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking for master order:', error);
      return { success: false, masterOrder: null };
    }
  }
}

// Initialize API handler
window.splitPaymentAPI = new SplitPaymentAPI();

// Export for use in split-payment.js
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SplitPaymentAPI;
}